import { apiClient } from "@/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  invitationQueryOptions,
  sessionQueryOptions,
} from "@/lib/query-options";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Navigate,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { z } from "zod";

const invitationSearchSchema = z.object({
  redirectTo: z.string().optional(),
});

export const Route = createFileRoute(
  "/(dashboard)/invitation/$invitationCode/",
)({
  validateSearch: zodValidator(invitationSearchSchema),
  beforeLoad: async ({ context, params }) => {
    try {
      const invitation = await context.queryClient.fetchQuery(
        invitationQueryOptions(params.invitationCode),
      );

      if (!invitation) {
        throw new Error("Invitation not found or expired");
      }
    } catch (error) {
      throw redirect({
        to: "/",
        search: {
          redirectTo: location.href,
        },
      });
    }
  },
  component: InvitationComponent,
  errorComponent: () => {
    return (
      <Navigate
        to="/login"
        params={{ error: "invalid_params", redirectTo: location.href }}
      />
    );
  },
});

function InvitationComponent() {
  const { redirectTo } = Route.useSearch();
  const { invitationCode } = Route.useParams();
  const navigate = useNavigate();
  const { data: invitation } = useSuspenseQuery(
    invitationQueryOptions(invitationCode),
  );
  const { data: session } = useSuspenseQuery(sessionQueryOptions);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAcceptInvitation = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await apiClient.v1.invite[":invitationCode"].$post({
        param: { invitationCode },
      });
      if (!response.ok) {
        throw new Error((await response.json()).message);
      }
      navigate({ to: redirectTo ?? "/" });
    } catch (err) {
      console.error("Error accepting invitation:", err);
      setError(
        err instanceof Error ? err.message : "Error al aceptar la invitación",
      );
    }
    setLoading(false);
  };

  const handleSignIn = async () => {
    //setError(null);
    //setLoading(true);
    navigate({
      to: "/login",
      search: { redirectTo: location.pathname + location.search },
    });
    //setLoading(false);
  };

  const handleGoBack = async () => {
    //setError(null);
    //setLoading(true);
    navigate({
      to: "/",
    });
    //setLoading(false);
  };

  return (
    <div className="min-h-screen bg-blue-100 dark:bg-slate-900 flex flex-col">
      {/* Mobile fullscreen layout */}
      <div className="flex-1 flex items-center justify-center md:p-8">
        <div className="w-full max-w-md">
          <Card className="h-screen md:h-auto md:shadow-xl border-0 md:border bg-white dark:bg-slate-800 md:bg-white/95 md:dark:bg-slate-800/95 md:backdrop-blur md:rounded-lg rounded-none">
            <CardHeader className="text-center space-y-6 pb-4 px-6 md:px-8 pt-8 md:pt-6">
              {/* Logo */}
              <div className="flex justify-center">
                <img src="/logo.svg" alt="Eiralab" className="h-12 w-auto" />
              </div>

              {/* Title and Description */}
              <div className="space-y-2">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {!invitation.isExpired
                    ? "🎉 ¡Estás invitado!"
                    : "⏰ Invitación expirada"}
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-400">
                  {!invitation.isExpired ? (
                    <>
                      <span className="font-medium">
                        {session?.user.email ?? "Hola"}
                      </span>
                      , has sido invitado a unirte a{" "}
                      <span className="font-medium">
                        {invitation.companyName}
                      </span>{" "}
                      como{" "}
                      <span className="font-medium capitalize">
                        {invitation.role === "admin"
                          ? "administrador"
                          : invitation.role === "manager"
                            ? "manager"
                            : invitation.role === "member"
                              ? "miembro"
                              : invitation.role}
                      </span>
                      {invitation.departmentNames.length > 0 && (
                        <>
                          {" "}
                          en{" "}
                          {invitation.departmentNames.length > 1
                            ? "los departamentos"
                            : "el departamento"}{" "}
                          <span className="font-medium">
                            {invitation.departmentNames.join(", ")}
                          </span>
                        </>
                      )}
                      .
                    </>
                  ) : (
                    "Esta invitación ha expirado y ya no es válida."
                  )}
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 px-6 md:px-8">
              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg border border-red-200 dark:border-red-800">
                  {error}
                </div>
              )}

              {!invitation.isExpired ? (
                session ? (
                  <Button
                    onClick={handleAcceptInvitation}
                    disabled={loading}
                    size="lg"
                    className="w-full"
                  >
                    {loading ? "Aceptando..." : "Aceptar invitación"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSignIn}
                    disabled={loading}
                    size="lg"
                    className="w-full"
                  >
                    Iniciar sesión para aceptar
                  </Button>
                )
              ) : (
                session && (
                  <Button
                    onClick={handleGoBack}
                    disabled={loading}
                    variant="outline"
                    size="lg"
                    className="w-full"
                  >
                    {loading ? "Regresando..." : "Volver al inicio"}
                  </Button>
                )
              )}
            </CardContent>

            <CardFooter className="pt-6 px-6 md:px-8">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center w-full">
                {!invitation.isExpired ? (
                  <>
                    Al continuar, aceptas nuestros{" "}
                    <a
                      href="https://hsechain.com/service-terms/"
                      className="text-primary hover:text-primary/80 underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      términos de servicio
                    </a>
                    .
                  </>
                ) : (
                  "Por favor contacta a tu administrador para obtener una nueva invitación."
                )}
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
